<template>
  <div class="tab-info">
    <div class="container">
      <div class="tab-info__content">
        <h3 v-if="heading"
            class="tab-info__heading">
          {{ heading }}
        </h3>

        <tabbed-layout id="tab-info-parent"
                       @changed="closeSubTabs">
          <template #selector="{currentTab, tabs, changeTab}">
            <dropdown-tab-selector
              id="tab-info-parent"
              :value="currentTab"
              :label="heading"
              :tabs="tabs"
              mobile-dropdown
              @tab-selected="val => changeTab(val)">
            </dropdown-tab-selector>
          </template>
          <tab-item v-for="(tab, index) in tabs"
                    :key="index"
                    :label="tab.label">
            <div class="row">
              <div class="col-lg-6">
                <div class="tab-info__text-container">
                  <h4 class="tab-info__content-heading">
                    {{ tab.label }}
                  </h4>
                  <!-- eslint-disable vue/no-v-html -->
                  <div class="tab-info__text"
                       v-html="tab.description" />
                  <!-- eslint-enable vue/no-v-html -->

                  <a
                    v-if="(tab.buttonLabel && tab.externalUrl) || tab.linkedContent"
                    class="tab-info__external-link"
                    :href="tab.externalUrl || tab.linkedContent">
                    {{ tab.buttonLabel }}
                    <font-awesome-icon
                      :icon="['fas', 'chevron-right']"
                      aria-hidden="true"></font-awesome-icon>
                  </a>
                  <button
                    v-if="tab.buttonLabel && tab.subTabs"
                    class="tab-info__sub-tab-button btn btn-accent-1"
                    @click="openSubTabs(tab)">
                    <span>{{ tab.buttonLabel }}</span>
                    <font-awesome-icon
                      :icon="['fas', 'chevron-right']"
                      aria-hidden="true"></font-awesome-icon>
                  </button>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="tab-info__image-wrapper">
                  <image-frame :image="tab.image"
                               :frame="tab.frame"></image-frame>
                </div>
              </div>
            </div>

            <tabbed-layout v-if="subTabs"
                           slim-tab-selector
                           class="tab-info__sub-tabs">
              <template #selector="{currentTab, tabs, changeTab}">
                <dropdown-tab-selector
                  id="tab-info-child"
                  :value="currentTab"
                  :label="heading"
                  :tabs="tabs"
                  mobile-dropdown
                  @tab-selected="val => changeTab(val)">
                </dropdown-tab-selector>
              </template>
              <tab-item
                v-for="(subTab, subTabIndex) in subTabs"
                :key="subTabIndex"
                :label="subTab.label">
                <!-- eslint-disable vue/no-v-html -->
                <div class="tab-info__sub-tab-text"
                     v-html="subTab.description" />
                <!-- eslint-enable vue/no-v-html -->
                <image-frame :image="subTab.image"></image-frame>
              </tab-item>
            </tabbed-layout>
          </tab-item>
        </tabbed-layout>
      </div>
    </div>
  </div>
</template>

<script>
import { TabbedLayout, TabItem } from "spiritweb.packages.web-account";
import ImageFrame from "~/Components/Products/Tabs/ImageFrame.vue";
import DropdownTabSelector from "~/Components/Products/Tabs/DropdownTabSelector.vue";

export default {
  name: "TabInfo",
  components: {
    TabbedLayout,
    TabItem,
    ImageFrame,
    DropdownTabSelector,
  },
  props: {
    heading: {
      type: String,
      default: null,
    },
    tabs: {
      type: Array,
      default: null,
    },
  },
  data () {
    return {
      subTabs: null,
    };
  },
  methods: {
    buttonUrl ( tab ) {
      let result = tab.externalLink;

      if ( tab.linkedContent ) {
        result = tab.linkedContent;
      }

      return result;
    },
    openSubTabs ( tab ) {
      this.subTabs = tab.subTabs;
    },
    closeSubTabs () {
      this.subTabs = null;
    },
  },
};
</script>

<style lang="less">
.tab-info {
  background-color: #ebebeb;
  max-width: 1500px;
  margin: 0 auto;

  &__content {
    min-height: 800px;
    display: flex;
    flex-direction: column;
    padding: 40px 0;
  }

  .tab-container {
    display: flex;
    font-size: 1.25rem;
    font-weight: 500;
    line-height: normal;
    color: #848484;

    @media (min-width: 767px) {
      margin: 0 -20px;
    }

    &--slim {
      justify-content: center;
    }

    .tab {
      display: flex;
      position: relative;
      transition: color 0.2s ease-out;
      padding: 0 20px;
      padding-bottom: 20px;

      &::after {
        content: "";
        display: block;
        height: 3px;
        width: calc(100% - 40px);
        background-color: #d7d7d7;
        position: absolute;
        bottom: 0;
        transition: background-color 0.2s ease-out;
      }

      &:first-child {
        margin-left: 0;
      }

      &:last-child {
        margin-right: 0;
      }

      &:hover {
        cursor: pointer;
        color: #333;
        &::after {
          background-color: #333;
        }
      }

      &--slim {
        .tab__label {
          width: 100%;
          font-size: 1rem;
          text-align: center;
          font-weight: 200;
          margin-bottom: 10px;
        }
      }

      &--selected {
        color: #d71920;

        &::after {
          background-color: #d71920;
        }

        &:hover {
          color: #d71920;

          &::after {
            background-color: #d71920;
          }
        }
      }

      .tab--selected.tab--slim {
        color: #333;
      }
    }
  }

  .tabbed-layout {
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    &__content {
      display: flex;
      flex-grow: 1;
      align-items: center;
    }
  }

  .tab-content {
    margin: 20px 0;
  }

  &__heading {
    font-weight: 500;
    font-size: 2.25rem;
    color: #333;
    margin-bottom: 40px;
  }

  &__text-container {
    display: flex;
    height: 100%;
    flex-direction: column;
    justify-content: center;
  }

  &__image-wrapper {
    @media (max-width: 991px) {
      margin-top: 40px;
    }
  }

  &__text {
    margin-bottom: 10px;
  }

  &__content-heading {
    font-size: 2.25rem;
    font-weight: 500;
    line-height: normal;
    margin-bottom: 20px;
  }

  &__external-link {
    text-decoration: none;
    font-size: 1.13rem;
    letter-spacing: 0.5px;

    &:hover {
      text-decoration: none;
    }
  }

  &__sub-tab-button {
    width: 300px;
    display: flex;
    justify-content: space-between;
  }

  &__sub-tab-text {
    margin: 20px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 100px;
    text-align: center;

    @media (max-width: 991px) {
      padding: 20px;
    }
  }

  &__sub-tabs {
    margin-top: 40px;
  }
}
</style>
